import { hasIntervalLimit } from 'src/models/user-package/utils';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import { removeReadOnlyFields } from 'src/utils/fieldNormalizers';


import { Package, PackageSearchResult, ServiceCount } from './_types';

const readOnlyFields = [
  'tags',
  'image_url',
  'package_limit_booking',
  'required_assessment_id',
  'location_name',
  'assigned_trainer_name',
];

// Fields to strip from service_counts before sending to the API.
// "ReadOnly" means React never writes these back — either they belong to the
// legacy Ember app or they are purely UI-side state with no backend counterpart.
const serviceCountReadOnlyFields = [
  'cadence', // managed by legacy Ember; React does not expose this field, must not overwrite it
  'count1',  // UI-only virtual field driving the "unlimited" toggle; has no backend counterpart
  '_limit_booking',
  '_limitBooking',
  '_unlimited',
];

const toArray = (value: any) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];

  return [value];
};

const normalizeAlternatePricesForForm = (alternatePrices: any[] = []) => {
  return alternatePrices.map((alternatePrice) => ({
    ...alternatePrice,
    lifecycleStageIds: toArray(alternatePrice.lifecycleStageIds),
    locationId: alternatePrice.locationId
      ? String(alternatePrice.locationId)
      : '',
    processorPlan: alternatePrice.processorPlan || '',
    requiredPackageIds: toArray(alternatePrice.requiredPackageIds),
  }));
};

export const normalizeAlternatePricesForRequest = (
  alternatePrices: any[] = []
) => {
  return alternatePrices.map((alternatePrice) => {
    const normalizedAlternatePrice = {
      ...alternatePrice,
      required_package_ids: toArray(alternatePrice.required_package_ids),
    };

    delete normalizedAlternatePrice.created_at;
    delete normalizedAlternatePrice.updated_at;

    return normalizedAlternatePrice;
  });
};

// Use TypeScript's type annotation to enforce the type of the input parameter
// and the return type of the function.
export function normalizePackage(pkg: any): Package {
  const camelized = camelcaseKeys(pkg, { deep: true }) as Package;

  camelized.serviceCounts?.forEach((serviceCount: ServiceCount) => {
    if (serviceCount.maxRollover) {
      serviceCount.rolloverVisits = parseInt(serviceCount.maxRollover.toString());
    }

    if (!serviceCount.cadence) {
      const legacyMixed =
        serviceCount.count > 0 && hasIntervalLimit(serviceCount);

      if (legacyMixed) {
        serviceCount.cadence = null;
      } else {
        const isCustomInterval =
          !!serviceCount.visitsInterval && serviceCount.count === 0;
        if (isCustomInterval) {
          serviceCount.cadence = 'custom';
        } else if (serviceCount.count > 0) {
          serviceCount.cadence = 'hard_limit';
        } else {
          serviceCount.cadence = 'follow_plan';
        }
      }
    }

    serviceCount._unlimited = serviceCount.count === 0;

    const isCustomInterval =
      serviceCount.cadence === 'custom' ||
      (!!serviceCount.visitsInterval && serviceCount.count === 0);

    serviceCount._limitBooking =
      serviceCount.count > 0 ||
      (isCustomInterval && !!serviceCount.maxVisits) ||
      (serviceCount.cadence === 'follow_plan' && !!serviceCount.maxVisits);
  });

  if (camelized.alternatePrices) {
    camelized.alternatePrices = normalizeAlternatePricesForForm(
      camelized.alternatePrices
    );
  }

  if (camelized.requiredAssessment) {
    camelized.requiredAssessment = parseInt(camelized.requiredAssessment.toString());
  }

  if (camelized.requiredSequence) {
    camelized.requiredSequence = parseInt(camelized.requiredSequence.toString());
  }

  return camelized;
}

// Here, Partial<Package> is used to signify that the function can accept
// an object that may not have all the fields from Package.
// The return type is set to any as the shape of the object can vary after decamelization.
export function denormalizePackage(pkg: Partial<Package>): any {
  const pack = decamelizeKeys(pkg, { deep: true });

  if (pack.tags?.length) {
    pack.tag_list = pack.tags.join(',');
  }

  if (pack.one_time_payment === '') {
    pack.one_time_payment = null;
  }

  pack.service_counts?.forEach((serviceCount: ServiceCount) => {
    removeReadOnlyFields(serviceCount, serviceCountReadOnlyFields);
  });

  if (pack.alternate_prices) {
    pack.alternate_prices = normalizeAlternatePricesForRequest(
      pack.alternate_prices
    );
  }

  removeReadOnlyFields(pack, readOnlyFields);

  return pack;
}

export function normalizePackageSearchResult(result: any): PackageSearchResult {
  const { json } = result.data;
  const camelized = camelcaseKeys(json);
  const searchResult: PackageSearchResult = {
    id: camelized.id,
    name: result.term,
    aliases: result.aliases,
  };

  return searchResult;
}

export interface BulkPackagesPayload {
  paymentMethod: string;
  clientIds: any[];
  packages: Array<{
    packageId: any;
    quantity?: number;
    paymentOptionId?: string;
    customPrice?: number; // Custom price in dollars (for purchases)
    customPlanId?: string; // Custom Stripe plan ID (for subscriptions)
    activationDate?: Date;
    expiresOn?: Date;
    couponId?: any;
    chargedById?: any;
    primaryCommissioneeIds?: any[];
    secondaryCommissioneeIds?: any[];
    cancelDate?: Date;
    billingCycleAnchor?: any;
    overrideProrationBehavior?: any;
    backdateIsStartdate?: boolean;
  }>;
}

export function denormalizeBulkPackages(payload: BulkPackagesPayload): any {
  // Map payment method to match v2 format
  const paymentMethodMap: Record<string, string> = {
    card_on_file: 'card',
    cash: 'cash',
    check: 'check',
    free: 'free',
    other: 'other',
  };

  const camelCasePayload = {
    paymentMethod:
      paymentMethodMap[payload.paymentMethod] || payload.paymentMethod,
    chargeUsers: payload.clientIds,
    chargeRequestId: Date.now(), // Timestamp for duplicate prevention
    existingPackages: payload.packages.map((pkg) => {
      const packageConfig = {
        // Use 'id' instead of 'packageId' to match v2 format
        id: pkg.packageId,
        quantity: pkg.quantity || 1,
        paymentOptionId: pkg.paymentOptionId,
        // Custom pricing fields
        customPrice: pkg.customPrice, // Amount in dollars for custom-priced purchases
        customPlanId: pkg.customPlanId, // Stripe plan ID for custom-priced subscriptions
        activationDate: pkg.activationDate,
        expiresOn: pkg.expiresOn,
        // Always include these fields as they're expected in v2
        coupon: pkg.couponId || null,
        editVisits: null,
        backdateAnchorDate: null,
        userIds: [], // Empty array as shown in v2 format
        chargedById: pkg.chargedById,
        primaryCommissioneeIds: pkg.primaryCommissioneeIds || [],
        secondaryCommissioneeIds: pkg.secondaryCommissioneeIds || [],
        cancelDate: pkg.cancelDate,
        billingCycleAnchor: pkg.billingCycleAnchor,
        overrideProrationBehavior: pkg.overrideProrationBehavior,
        backdateIsStartdate: pkg.backdateIsStartdate || false,
      };

      // Remove undefined/null values before converting to snake_case
      return Object.fromEntries(
        Object.entries(packageConfig).filter(
          ([_, value]) => value !== undefined && value !== null
        )
      );
    }),
  };

  // Convert to snake_case for API
  const apiPayload = decamelizeKeys(camelCasePayload, { deep: true });

  // Stringify existing_packages AFTER decamelizing because PackageAssigner expects a JSON string
  apiPayload.existing_packages = JSON.stringify(apiPayload.existing_packages);

  return apiPayload;
}
